import Image from '@/components/config-editor/form-elements/Image';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from "@/lib/utils";
import type { FieldProps } from '@rjsf/utils';
import { Check, CreditCard, Edit2Icon, Search } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Card network display names
const CARD_NETWORK_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  maestro: 'Maestro',
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  elo: 'Elo',
  unionpay: 'UnionPay',
  mir: 'Mir',
  rupay: 'RuPay',
};

// Card network image paths
const CARD_NETWORK_IMAGES: Record<string, string> = {
  visa: '/card-networks/visa.svg',
  mastercard: '/card-networks/mastercard.svg',
  maestro: '/card-networks/maestro.svg',
  amex: '/card-networks/amex.svg',
  diners: '/card-networks/dinersclub.svg',
  discover: '/card-networks/discover.svg',
  jcb: '/card-networks/jcb.svg',
  elo: '/card-networks/elo.svg',
  unionpay: '/card-networks/unionpay.svg',
  mir: '/card-networks/mir.svg',
  rupay: '/card-networks/rupay.svg',
};

// Card network categories for better organization
const CARD_NETWORK_CATEGORIES: Record<string, string[]> = {
  '💳 Major Networks': ['visa', 'mastercard', 'amex', 'discover'],
  '🌍 Regional Networks': ['elo', 'rupay', 'mir', 'unionpay'],
  '🏦 Other Networks': ['maestro', 'diners', 'jcb'],
};

// Predefined network IDs (from CardNetwork enum)
const PREDEFINED_NETWORK_IDS = new Set([
  'visa',
  'mastercard',
  'maestro',
  'amex',
  'diners',
  'discover',
  'jcb',
  'elo',
  'unionpay',
  'mir',
  'rupay',
]);

/**
 * Generate ID from name (slugify)
 */
function generateIdFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Check if value is a predefined network object { id: string }
 */
function isPredefinedNetwork(value: any): value is { id: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    !('name' in value) &&
    !('imageUrl' in value) &&
    PREDEFINED_NETWORK_IDS.has(value.id)
  );
}

/**
 * Check if value is a custom network object
 */
function isCustomNetwork(value: any): value is { imageUrl: string; name: string; id: string } {
  return (
    typeof value === 'object' && value !== null && 'imageUrl' in value && 'name' in value && 'id' in value
  );
}

type CardNetworkValue = { id: string } | { id: string; name: string; imageUrl: string } | null | undefined;

/**
 * CardNetworkSelectorField for selecting card networks with visual preview and search
 */
const CardNetworkSelectorField = forwardRef<HTMLDivElement, FieldProps<CardNetworkValue>>((props, ref) => {
  const {
    formData,
    onChange,
    schema,
    disabled,
    readonly,
    fieldPathId: { path },
    name,
  } = props;

  const { t } = useTranslation('storefront');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customName, setCustomName] = useState('');
  const [customImageUrl, setCustomImageUrl] = useState('');

  const id = path.join('.');

  // Get available card networks from schema enum or use all networks
  const availableNetworks = useMemo(() => {
    if (schema.enum && Array.isArray(schema.enum)) {
      return schema.enum as string[];
    }
    return Object.keys(CARD_NETWORK_IMAGES);
  }, [schema.enum]);

  // Filter networks based on search query
  const filteredNetworks = useMemo(() => {
    if (!searchQuery) return availableNetworks;
    const query = searchQuery.toLowerCase();
    return availableNetworks.filter((network) => {
      const networkName = CARD_NETWORK_NAMES[network] || network;
      return networkName.toLowerCase().includes(query) || network.toLowerCase().includes(query);
    });
  }, [availableNetworks, searchQuery]);

  // Group filtered networks by category
  const groupedNetworks = useMemo(() => {
    const groups: Record<string, string[]> = {};

    // First, add networks to their categories
    for (const [category, networks] of Object.entries(CARD_NETWORK_CATEGORIES)) {
      const filtered = networks.filter((network) => filteredNetworks.includes(network));
      if (filtered.length > 0) {
        groups[category] = filtered;
      }
    }

    // Add any remaining networks that aren't in a category
    const categorizedNetworks = Object.values(CARD_NETWORK_CATEGORIES).flat();
    const uncategorized = filteredNetworks.filter((network) => !categorizedNetworks.includes(network));
    if (uncategorized.length > 0) {
      groups['Other'] = uncategorized;
    }

    return groups;
  }, [filteredNetworks]);

  const handleSelectPredefined = (network: string) => {
    // Return { id: network } for predefined networks
    onChange({ id: network }, path);
    setOpen(false);
    setSearchQuery('');
  };

  const handleSelectCustom = () => {
    if (!customName.trim()) return;

    const generatedId = generateIdFromName(customName);
    onChange(
      {
        imageUrl: customImageUrl.trim() || '',
        name: customName.trim(),
        id: generatedId,
      },
      path,
    );
    setOpen(false);
    setSearchQuery('');
    setCustomName('');
    setCustomImageUrl('');
  };

  // Initialize custom fields if custom network is selected
  useEffect(() => {
    if (isCustomNetwork(formData)) {
      setCustomName(formData.name);
      setCustomImageUrl(formData.imageUrl);
    } else if (!isPredefinedNetwork(formData)) {
      // Only clear if not a predefined network (i.e., no selection or invalid value)
      setCustomName('');
      setCustomImageUrl('');
    }
  }, [formData]);

  // Determine selected network display
  const selectedNetworkImage = useMemo(() => {
    if (isPredefinedNetwork(formData)) {
      return CARD_NETWORK_IMAGES[formData.id] || null;
    }
    if (isCustomNetwork(formData)) {
      return formData.imageUrl || null;
    }
    return null;
  }, [formData]);

  const selectedNetworkName = useMemo(() => {
    if (isPredefinedNetwork(formData)) {
      return CARD_NETWORK_NAMES[formData.id] || formData.id;
    }
    if (isCustomNetwork(formData)) {
      return formData.name;
    }
    return null;
  }, [formData]);

  const isPredefinedSelected = isPredefinedNetwork(formData);
  const isCustomSelected = isCustomNetwork(formData);

  return (
    <div ref={ref} className="space-y-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || readonly}
            className={cn('h-10 w-full justify-between', !formData && 'text-muted-foreground')}
          >
            <div className="flex items-center gap-2">
              {selectedNetworkImage ? (
                <img
                  src={selectedNetworkImage}
                  alt={selectedNetworkName || ''}
                  className="h-5 w-auto"
                  onError={(e) => {
                    // Hide image if it fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              <span>
                {selectedNetworkName ||
                  (isCustomSelected ? (formData as { name: string }).name : '') ||
                  (isPredefinedSelected ? (formData as { id: string }).id : '') ||
                  t('configEditor.cardNetwork.selectCardNetwork')}
              </span>
            </div>
            <Edit2Icon className={cn('ml-2 h-4 w-4 shrink-0 opacity-50')} />
          </Button>
        </DialogTrigger>
        <DialogContent className="w-full max-w-[calc(100%-1rem)] sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('configEditor.cardNetwork.selectCardNetworkTitle')}</DialogTitle>
            <DialogDescription>{t('configEditor.cardNetwork.selectCardNetworkDesc')}</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="predefined" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="predefined" className="flex-1">
                {t('configEditor.cardNetwork.predefined')}
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex-1">
                {t('configEditor.cardNetwork.custom')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="predefined" className="m-0">
              {/* Search input */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                  <Input
                    placeholder={t('configEditor.cardNetwork.searchCardNetworks')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 pl-9"
                    autoFocus
                  />
                </div>
              </div>

              {/* Network grid */}
              <div className="max-h-[400px] overflow-y-auto overscroll-contain">
                <div className="space-y-4">
                  {Object.entries(groupedNetworks).map(([category, networks]) => (
                    <div key={category}>
                      <Label className="text-muted-foreground mb-3 block text-xs font-medium">
                        {category}
                      </Label>
                      <div className="grid grid-cols-4 gap-3">
                        {networks.map((network) => {
                          const networkImage = CARD_NETWORK_IMAGES[network];
                          const networkName = CARD_NETWORK_NAMES[network] || network;
                          const isSelected =
                            isPredefinedSelected && (formData as { id: string }).id === network;

                          return (
                            <button
                              key={network}
                              type="button"
                              onClick={() => handleSelectPredefined(network)}
                              className={cn(
                                'relative flex flex-col items-center justify-center rounded-lg border p-4 transition-all',
                                'hover:bg-accent hover:text-accent-foreground hover:border-primary/50',
                                'focus:ring-ring focus:outline-none focus:ring-2 focus:ring-offset-2',
                                isSelected && 'border-primary bg-primary/10 shadow-sm',
                              )}
                              title={networkName}
                            >
                              {networkImage ? (
                                <img
                                  src={networkImage}
                                  alt={networkName}
                                  className="mb-2 h-10 w-auto max-w-full"
                                  onError={(e) => {
                                    // Hide image if it fails to load
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <CreditCard className="text-muted-foreground mb-2 h-10 w-10" />
                              )}
                              <span className="text-center text-xs font-medium leading-tight">
                                {networkName}
                              </span>
                              {isSelected && (
                                <div className="absolute right-2 top-2">
                                  <div className="bg-primary flex h-5 w-5 items-center justify-center rounded-full">
                                    <Check className="text-primary-foreground h-3 w-3" />
                                  </div>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {Object.keys(groupedNetworks).length === 0 && (
                    <div className="text-muted-foreground py-12 text-center text-sm">
                      {t('configEditor.cardNetwork.noNetworksFound', { query: searchQuery })}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="custom" className="m-0">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="custom-name">{t('configEditor.cardNetwork.networkName')}</Label>
                  <Input
                    id="custom-name"
                    placeholder="e.g., Custom Payment Network"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    disabled={disabled || readonly}
                  />
                  <p className="text-muted-foreground text-xs">
                    ID will be auto-generated: {customName ? generateIdFromName(customName) : '...'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-image">{t('configEditor.cardNetwork.imageUrl')}</Label>
                  <Image
                    id="custom-image"
                    placeholder="https://example.com/logo.svg"
                    value={customImageUrl}
                    onChange={(value) => setCustomImageUrl(value)}
                  />
                </div>

                <Button
                  onClick={handleSelectCustom}
                  disabled={disabled || readonly || !customName.trim()}
                  className="w-full"
                >
                  <Check className="mr-2 h-4 w-4" />
                  {t('configEditor.cardNetwork.validateCustom')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Selected network preview */}
          {(isPredefinedSelected || isCustomSelected) && (
            <div className="bg-muted/50 mt-4 rounded-lg border p-3">
              <div className="flex items-center gap-2 text-sm">
                {selectedNetworkImage ? (
                  <img src={selectedNetworkImage} alt={selectedNetworkName || ''} className="h-5 w-auto" />
                ) : (
                  <CreditCard className="h-5 w-5" />
                )}
                <span className="font-medium">{selectedNetworkName}</span>
                {isPredefinedSelected && (
                  <span className="text-muted-foreground text-xs">({(formData as { id: string }).id})</span>
                )}
                {isCustomSelected && (
                  <span className="text-muted-foreground text-xs">({(formData as { id: string }).id})</span>
                )}
                <span className="text-muted-foreground ml-auto">{t('configEditor.cardNetwork.selected')}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

CardNetworkSelectorField.displayName = 'CardNetworkSelectorField';

export default CardNetworkSelectorField;
